import React, { createContext, useContext, useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert'; // Added for displaying the message
import dayjs from 'dayjs';
import axios from 'axios';
import apiClient from '../../../pages/keycloak/axiosInstance';

// Define Context in the same file
const PalletContext = createContext();
export const usePallet = () => useContext(PalletContext);

const PalletAnalysis = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiMessage, setApiMessage] = useState(null); // State for API message
  const [selectedPalletNumber, setSelectedPalletNumber] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // API call to fetch data
  useEffect(() => {
    const fetchPalletData = async () => {
      const projectId = localStorage.getItem('selectedProject');
      const supplierName = sessionStorage.getItem('selectedSupplierTab');
      setLoading(true);
      setApiMessage(null); // Clear previous message
      try {
        const response = await apiClient.post(`get_flagged_modules`, {
          projectId,
          supplierName,
        });
        // Extract message and data from response
        const { message, data } = response.data;
        setApiMessage(message || 'Data retrieved'); // Fallback message if none provided
        // Map API response to match table structure
        const formattedData = data.map((item, index) => ({
          srNo: index + 1,
          palletNumber: item['Pallet Number'],
          orderNo: item['Order Number'],
          invoiceNo: item['Invoice Number'] || 'N/A',
          vehicleNo: item['Vehicle Number'],
          receivedOn: item['Received Date'],
          shippedOn: item['Date of Shipping'],
          status: item['Action'] || 'N/A',
          acceptedQuantity: item['Quantity'],
        }));
        setTableData(formattedData);
      } catch (err) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch pallet data';
        setError(errorMessage);
        setApiMessage(errorMessage); // Show API error message
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPalletData();
  }, []);

  // Filter data based on date range and search query
  const filteredData = tableData.filter((row) => {
    const rowDate = dayjs(row.shippedOn);
    const withinDateRange =
      (!startDate || rowDate.isAfter(dayjs(startDate).subtract(1, 'day'))) &&
      (!endDate || rowDate.isBefore(dayjs(endDate).add(1, 'day')));
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      searchQuery === '' ||
      Object.values(row).some(
        (value) =>
          typeof value === 'string' &&
          value.toLowerCase().includes(searchLower)
      );
    return withinDateRange && matchesSearch;
  });

  const handleFlaggedPallet = (row) => {
    setSelectedPalletNumber(row.palletNumber);
    sessionStorage.setItem('flaggedPalletData', JSON.stringify(row)); // Optional
    window.location.href = '/module-flagged-form';
  };

  return (
    <PalletContext.Provider value={{ selectedPalletNumber, setSelectedPalletNumber }}>
      <div className="min-h-screen bg-gray-100 p-6">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-6">
          Flagged Pallet Analysis
        </h1>
        <div className="relative w-full mt-1 mb-4">
          <input
            type="text"
            placeholder="Search by any field..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
          />
          <svg
            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
            />
          </svg>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="flex gap-4 bg-white p-2 rounded-lg shadow-md">
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => (
                  <TextField {...params} size="small" className="bg-white" />
                )}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => (
                  <TextField {...params} size="small" className="bg-white" />
                )}
              />
            </div>
          </LocalizationProvider>
        </div>

        {/* Display API message */}
        {apiMessage && (
          <Alert
            severity={error ? 'error' : 'info'}
            sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}
          >
            {apiMessage}
          </Alert>
        )}

        {loading && <div className="text-center">Loading...</div>}

        <div className="overflow-x-auto bg-white shadow-md rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-700">
              <tr>
                {[
                  'Sr.no',
                  'Pallet Number',
                  'Received On',
                  'Vehicle No',
                  'Quantity',
                  'Order No',
                  'Invoice No',
                  'Shipped On',
                  'Status',
                  'Action',
                ].map((header) => (
                  <th
                    key={header}
                    className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.length > 0 ? (
                filteredData.map((row) => (
                  <tr key={row.srNo}>
                    <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                      {row.srNo}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                      {row.palletNumber}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                      {row.receivedOn}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                      {row.vehicleNo}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                      {row.acceptedQuantity}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                      {row.orderNo}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                      {row.invoiceNo}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                      {row.shippedOn}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                      {row.status}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleFlaggedPallet(row)}
                        sx={{
                          backgroundColor: '#424752',
                          '&:hover': { backgroundColor: '#6c757d' },
                          padding: '4px 12px',
                          fontSize: '0.75rem',
                          lineHeight: '1.5',
                          textTransform: 'none',
                        }}
                      >
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="11"
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PalletContext.Provider>
  );
};

export default PalletAnalysis;